import { z } from 'zod';

/**
 * CLAPTAL Kids: the rules the backend, the Lab and the kids app share.
 *
 * Everything here was decided by the owner on October 6, 2026 (see docs/claptal-kids.md): one app
 * for ages 3 to 12, every topic open to every age and only the telling adapted to it, episodes of
 * at most 70 seconds that teach one thing, a series in the one language its idea was submitted in,
 * two hours a day with one extra hour a parent can unlock, and six visual styles.
 */

export const KIDS_LANGUAGES = ['fr', 'ar', 'en'] as const;
export type KidsLanguage = (typeof KIDS_LANGUAGES)[number];
export const KidsLanguageSchema = z.enum(KIDS_LANGUAGES);

/** How a series' language is named to the writers and to MiniMax H3's `<d>[Language]` tags. */
export const LANGUAGE_NAMES: Record<KidsLanguage, string> = {
  fr: 'French',
  // The owner chose fusha: understood across the Arab world and the norm for children's
  // educational programmes. The October 6 test shot proved H3 speaks it clearly.
  ar: 'Arabic',
  en: 'English',
};
/** What the writers are told about each language, beyond its name. */
export const LANGUAGE_WRITING: Record<KidsLanguage, string> = {
  fr: 'Write every word the audience hears or reads in natural, standard French as spoken to children in France and French-speaking Africa.',
  ar: 'Write every word the audience hears or reads in Modern Standard Arabic (fusha), simple and clear, with full vowel marks (tashkeel) on every spoken line so it is pronounced correctly. Never use a dialect.',
  en: 'Write every word the audience hears or reads in simple, standard English.',
};

export const KIDS_STYLES = ['3d', 'clay', 'storybook', 'paper', 'anime', 'cartoon'] as const;
export type KidsStyle = (typeof KIDS_STYLES)[number];
export const KidsStyleSchema = z.enum(KIDS_STYLES);
/**
 * Each style as the image and video models are told it. The October 6 stills showed each of these
 * reads distinctly; people are always stylised, never photographic.
 */
export const STYLE_LOOK: Record<KidsStyle, { name: string; look: string }> = {
  '3d': {
    name: '3D animation',
    look: 'a bright, warm 3D animated feature film for children: soft global illumination, expressive stylised faces with big eyes, rounded shapes, rich saturated colour',
  },
  clay: {
    name: 'clay stop-motion',
    look: 'handmade claymation stop-motion: characters and sets modelled in clay with visible fingerprints and tool marks, felt and wire details, a miniature handcrafted set, soft studio lighting',
  },
  storybook: {
    name: 'storybook illustration',
    look: "a painted children's picture book come to life in gouache and watercolour: visible brush texture and paper grain, a gentle warm palette, soft edges",
  },
  paper: {
    name: 'paper cut-out',
    look: 'layered paper cut-out like a shadow-theatre diorama: coloured card layers with soft drop shadows between them, patterned paper edges, gentle movement',
  },
  anime: {
    name: 'anime',
    look: 'a gentle Japanese anime film for children: clean line art, cel shading, big expressive eyes, painted backgrounds with soft clouds',
  },
  cartoon: {
    name: 'cartoon',
    look: 'a modern 2D television cartoon for young children: flat bright colours, simple rounded shapes, bold clean outlines, no gradients',
  },
};

export const KIDS_CATEGORIES = [
  'science',
  'wild',
  'time',
  'puzzles',
  'feelings',
  'tales',
  'how',
  'words',
  'bedtime',
] as const;
export type KidsCategory = (typeof KIDS_CATEGORIES)[number];
export const KidsCategorySchema = z.enum(KIDS_CATEGORIES);
/** The writers' brief for each category: what kind of thing an episode in it teaches. */
export const CATEGORY_BRIEF: Record<KidsCategory, string> = {
  science:
    'Science Explorers: space, the body, weather, light, simple experiments a child could try',
  wild: 'Wild Planet: animals, plants, habitats and caring for nature',
  time: 'Time Travelers: history told through people who were there',
  puzzles: 'Puzzle Detectives: logic, numbers, shapes and patterns the viewer solves with the hero',
  feelings: 'Feelings & Friends: naming emotions, empathy, sharing, solving a disagreement',
  tales:
    'World Tales: folktales and myths from Arab, French, African and other traditions, and the places they come from',
  how: 'How Things Work: inventions, machines and everyday objects',
  words: 'Word Quest: vocabulary, reading, letters and other languages',
  bedtime: 'Bedtime Stories: calm, gentle stories with a soft lesson and a quiet ending',
};

export const AGE_MIN = 3;
export const AGE_MAX = 12;
export const AGE_BANDS = [
  { id: 'little', min: 3, max: 5 },
  { id: 'explorers', min: 6, max: 8 },
  { id: 'discoverers', min: 9, max: 12 },
] as const;
export type AgeBand = (typeof AGE_BANDS)[number]['id'];
/** How each band is written for. Every topic is open to every age; only the telling changes. */
export const BAND_WRITING: Record<AgeBand, string> = {
  little:
    'Ages 3–5: a narrator or the hero speaks directly to the child; very simple words and short sentences; slow pace; repetition; one concrete idea a child can point at or count; nothing to read; gentle, never frightening.',
  explorers:
    'Ages 6–8: everyday vocabulary, a clear little problem and its solution, one surprising fact, short sentences; mild suspense resolved warmly.',
  discoverers:
    'Ages 9–12: richer vocabulary and a real plot, one idea explained properly with its why, humour and curiosity; stakes that feel real but stay safe.',
};

/** A child's age in whole years on `now`, from the birth month and year the parent gives. */
export function ageOn(birthYear: number, birthMonth: number, now: Date) {
  const years = now.getUTCFullYear() - birthYear;
  return now.getUTCMonth() + 1 < birthMonth ? years - 1 : years;
}
export function bandFor(age: number): AgeBand {
  const clamped = Math.min(AGE_MAX, Math.max(AGE_MIN, age));
  return AGE_BANDS.find((b) => clamped >= b.min && clamped <= b.max)!.id;
}
export function bandRange(band: AgeBand) {
  const found = AGE_BANDS.find((b) => b.id === band)!;
  return { ageMin: found.min, ageMax: found.max };
}
/** Whether a birth month and year give a child CLAPTAL Kids is made for. */
export function validBirth(birthYear: number, birthMonth: number, now: Date) {
  if (!Number.isInteger(birthYear) || !Number.isInteger(birthMonth)) return false;
  if (birthMonth < 1 || birthMonth > 12) return false;
  const age = ageOn(birthYear, birthMonth, now);
  return age >= 2 && age <= 13;
}
/**
 * How well a series suits a child, from 0 (not for them) upwards. The parent gives the age and
 * CLAPTAL decides: a series written for the child's age comes first, and one written for a year
 * either side is still offered after it, so a five-year-old curious about history is not left
 * with nothing.
 */
export function suitability(series: { ageMin: number; ageMax: number }, age: number) {
  if (age >= series.ageMin && age <= series.ageMax) return 2;
  if (age >= series.ageMin - 1 && age <= series.ageMax + 1) return 1;
  return 0;
}

/** Two hours of watching a day, then one extra hour a parent can unlock, once a day. */
export const DAILY_SECONDS = 2 * 3600;
export const EXTRA_SECONDS = 3600;
export function allowedSeconds(extraUnlocked: boolean) {
  return DAILY_SECONDS + (extraUnlocked ? EXTRA_SECONDS : 0);
}
/** The child's local calendar day, which is what "a day" means for the limit. */
export function dayKey(now: number, utcOffsetMinutes: number) {
  const offset = Math.max(-14 * 60, Math.min(14 * 60, Math.round(utcOffsetMinutes)));
  return new Date(now + offset * 60_000).toISOString().slice(0, 10);
}
/** The most one heartbeat may add: the app reports every fifteen seconds while playing. */
export const HEARTBEAT_MAX_SECONDS = 30;

/** Episodes are at most 70 seconds and teach one thing. */
export const KIDS_EPISODE_SECONDS = { min: 40, max: 70 } as const;
/** About 2.2 words a second for children, with room to look and react. */
export const KIDS_MAX_WORDS = 150;

/** The parent code: six digits, ten minutes, five tries. */
export const PARENT_CODE = { digits: 6, ttlMs: 10 * 60_000, attempts: 5 } as const;
export const CodeMethodSchema = z.enum(['push', 'sms']);
export type CodeMethod = z.infer<typeof CodeMethodSchema>;

/** What an episode teaches and the one question after it, written by the writers' room. */
export const KidsLearningSchema = z.object({
  point: z
    .string()
    .min(3)
    .max(160)
    .describe('The one thing a child learns, as a short phrase in the series language.'),
  question: z
    .string()
    .min(3)
    .max(160)
    .describe('One question about it, in the series language, answerable from the episode.'),
  answers: z
    .array(z.string().min(1).max(60))
    .length(3)
    .describe('Three short answers in the series language; exactly one is right.'),
  correct: z.number().int().min(0).max(2).describe('The index of the right answer.'),
});
export type KidsLearning = z.infer<typeof KidsLearningSchema>;

type Labels = Record<KidsLanguage, string>;
export interface IdeaCard {
  id: string;
  emoji: string;
  labels: Labels;
  /** How the writers are told it, whatever language the child chose. */
  brief: string;
}
/**
 * The picture cards a child builds an idea from. Children choose; they never type or record, so
 * nothing they say about themselves enters the system, and nobody ever asks a child to reword.
 */
export const IDEA_CARDS = {
  hero: [
    {
      id: 'fox',
      emoji: '🦊',
      labels: { fr: 'Un renard', ar: 'ثعلب', en: 'A fox' },
      brief: 'a curious young fox',
    },
    {
      id: 'robot',
      emoji: '🤖',
      labels: { fr: 'Un robot', ar: 'روبوت', en: 'A robot' },
      brief: 'a small friendly robot',
    },
    {
      id: 'astronaut',
      emoji: '👩‍🚀',
      labels: { fr: 'Une astronaute', ar: 'رائدة فضاء', en: 'An astronaut' },
      brief: 'a girl who dreams of being an astronaut',
    },
    {
      id: 'camel',
      emoji: '🐪',
      labels: { fr: 'Un chameau', ar: 'جمل', en: 'A camel' },
      brief: 'a cheerful young camel',
    },
    {
      id: 'turtle',
      emoji: '🐢',
      labels: { fr: 'Une tortue', ar: 'سلحفاة', en: 'A turtle' },
      brief: 'a slow, wise sea turtle',
    },
    {
      id: 'inventor',
      emoji: '🧒',
      labels: { fr: 'Un petit inventeur', ar: 'مخترع صغير', en: 'A young inventor' },
      brief: 'a boy who invents things',
    },
  ],
  place: [
    {
      id: 'desert',
      emoji: '🏜️',
      labels: { fr: 'Le désert', ar: 'الصحراء', en: 'The desert' },
      brief: 'the desert',
    },
    {
      id: 'sea',
      emoji: '🌊',
      labels: { fr: 'La mer', ar: 'البحر', en: 'The sea' },
      brief: 'the sea and the beach',
    },
    {
      id: 'space',
      emoji: '🚀',
      labels: { fr: 'L’espace', ar: 'الفضاء', en: 'Space' },
      brief: 'outer space',
    },
    {
      id: 'medina',
      emoji: '🕌',
      labels: { fr: 'La médina', ar: 'المدينة القديمة', en: 'The old medina' },
      brief: 'an old medina with narrow streets and a market',
    },
    {
      id: 'forest',
      emoji: '🌳',
      labels: { fr: 'La forêt', ar: 'الغابة', en: 'The forest' },
      brief: 'a forest',
    },
    {
      id: 'school',
      emoji: '🏫',
      labels: { fr: 'L’école', ar: 'المدرسة', en: 'School' },
      brief: 'a school',
    },
  ],
  problem: [
    {
      id: 'lost',
      emoji: '🧭',
      labels: { fr: 'Il s’est perdu', ar: 'ضلّ الطريق', en: 'Got lost' },
      brief: 'the hero gets lost',
    },
    {
      id: 'dark',
      emoji: '🌑',
      labels: { fr: 'Il a peur du noir', ar: 'يخاف من الظلام', en: 'Afraid of the dark' },
      brief: 'the hero is afraid of the dark',
    },
    {
      id: 'vanished',
      emoji: '🔎',
      labels: { fr: 'Un objet a disparu', ar: 'اختفى شيء ما', en: 'Something vanished' },
      brief: 'something important has vanished',
    },
    {
      id: 'friend',
      emoji: '🤝',
      labels: { fr: 'Il cherche un ami', ar: 'يبحث عن صديق', en: 'Wants a friend' },
      brief: 'the hero wants a friend',
    },
    {
      id: 'broken',
      emoji: '🔧',
      labels: { fr: 'Quelque chose est cassé', ar: 'شيء ما انكسر', en: 'Something broke' },
      brief: 'something the hero needs is broken',
    },
    {
      id: 'storm',
      emoji: '⛈️',
      labels: { fr: 'Une tempête arrive', ar: 'عاصفة قادمة', en: 'A storm is coming' },
      brief: 'a storm is coming',
    },
  ],
  lesson: [
    {
      id: 'stars',
      emoji: '⭐',
      labels: { fr: 'Les étoiles guident', ar: 'النجوم تهدينا', en: 'Stars show the way' },
      brief: 'how the stars help us find our way',
    },
    {
      id: 'sharing',
      emoji: '🍎',
      labels: { fr: 'Partager', ar: 'المشاركة', en: 'Sharing' },
      brief: 'why sharing makes things better',
    },
    {
      id: 'courage',
      emoji: '🦁',
      labels: { fr: 'Le courage', ar: 'الشجاعة', en: 'Courage' },
      brief: 'what courage really is',
    },
    {
      id: 'water',
      emoji: '💧',
      labels: { fr: 'L’eau est précieuse', ar: 'الماء ثمين', en: 'Water is precious' },
      brief: 'why water is precious and where it comes from',
    },
    {
      id: 'counting',
      emoji: '🔢',
      labels: { fr: 'Compter', ar: 'العدّ', en: 'Counting' },
      brief: 'counting and numbers',
    },
    {
      id: 'plants',
      emoji: '🌱',
      labels: {
        fr: 'Comment poussent les plantes',
        ar: 'كيف تنمو النباتات',
        en: 'How plants grow',
      },
      brief: 'how plants grow from a seed',
    },
  ],
} satisfies Record<string, IdeaCard[]>;
export type IdeaCardKind = keyof typeof IDEA_CARDS;
export const IDEA_CARD_KINDS: IdeaCardKind[] = ['hero', 'place', 'problem', 'lesson'];
export const IdeaPicksSchema = z.object({
  hero: z.string(),
  place: z.string(),
  problem: z.string(),
  lesson: z.string(),
});
export type IdeaPicks = z.infer<typeof IdeaPicksSchema>;
export function cardFor(kind: IdeaCardKind, id: string): IdeaCard | undefined {
  return (IDEA_CARDS[kind] as IdeaCard[]).find((c) => c.id === id);
}
export function validPicks(picks: IdeaPicks) {
  return IDEA_CARD_KINDS.every((kind) => !!cardFor(kind, picks[kind]));
}
/** A child's picture-card idea as the premise the writers receive. */
export function picksPremise(picks: IdeaPicks) {
  const [hero, place, problem, lesson] = IDEA_CARD_KINDS.map((k) => cardFor(k, picks[k])!);
  return `A story about ${hero.brief} in ${place.brief}, where ${problem.brief}. Along the way the child watching learns ${lesson.brief}.`;
}
/** A child's picture-card idea as a short title in its own language. */
export function picksTitle(picks: IdeaPicks, language: KidsLanguage) {
  return `${cardFor('hero', picks.hero)!.labels[language]} · ${cardFor('place', picks.place)!.labels[language]}`;
}

/** A parent's written idea. */
export const PARENT_IDEA = { title: 60, premise: 300 } as const;

/**
 * The first-party usage events the kids app may send. On iOS these are the only measurement:
 * Apple's Kids Category forbids third-party analytics, and these stay in CLAPTAL's own database.
 */
export const KIDS_EVENTS = [
  'app_opened',
  'profile_selected',
  'episode_started',
  'episode_finished',
  'question_answered',
  'idea_submitted',
  'idea_voted',
  'limit_reached',
  'extra_hour_unlocked',
  'parent_area_opened',
] as const;
export const KidsEventSchema = z.object({
  name: z.enum(KIDS_EVENTS),
  platform: z.enum(['ios', 'android']),
  at: z.number(),
  props: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
});

/** Avatars a child profile can take: animals, never a photograph. */
export const KIDS_AVATARS = [
  'lion',
  'fox',
  'owl',
  'cat',
  'panda',
  'rabbit',
  'turtle',
  'whale',
] as const;
export const KidsAvatarSchema = z.enum(KIDS_AVATARS);
export const AVATAR_EMOJI: Record<(typeof KIDS_AVATARS)[number], string> = {
  lion: '🦁',
  fox: '🦊',
  owl: '🦉',
  cat: '🐱',
  panda: '🐼',
  rabbit: '🐰',
  turtle: '🐢',
  whale: '🐳',
};
